import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useEmployeeGrade } from "../../contexts/EmployeeGradesContext";
import Modal from "../../components/UI/Modal";
import WorkplaceForm from "../../components/Form/WorkplaceForm";
import ReusableTable from "../../components/UI/ReusableTable";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import { Description } from "@headlessui/react";
import { X, Briefcase, MoreVertical, Building, Calendar, Users, MapPin, Award, Clock, Grid } from 'lucide-react';
import DropdownMenu from "../../components/UI/DropdownMenu";
import EmployeeGradeForm from "../../components/Form/EmployeeGradeForm";

// Separate component for actions column to avoid hooks rule violation
const ActionsColumn = React.memo(({ row, openMenu, setOpenMenu, openModalForEdit, handleDeleteEmployeeGrade }) => {
  const buttonRef = useRef(null);
  
  return (
    <div className="flex items-center justify-center relative">
      <button
        ref={buttonRef}
        onClick={() =>
          setOpenMenu(openMenu === row.id ? null : row.id)
        }
        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
      >
        <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
      </button>

      {openMenu === row.id && (
        <DropdownMenu
          buttonRef={buttonRef}
          onClose={() => setOpenMenu(null)}
        >
          <button
            onClick={(e) => {
              openModalForEdit(row);
              setOpenMenu(null);
            }}
            className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
          >
            Edit
          </button>
          <DeleteButton
            style={`text-red-600 hover:text-red-800 px-3 py-2`}
            onDelete={(e) => {
              handleDeleteEmployeeGrade(row.id);
              setOpenMenu(null);
            }}
          />
        </DropdownMenu>
      )}
    </div>
  );
});

const EmployeeGradeMasters = React.memo(() => {
  const { employeeGrades, addEmployeeGrade, updateEmployeeGrade, deleteEmployeeGrade, isLoading } = useEmployeeGrade();
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEmployeeGrade, setEditingEmployeeGrade] = useState(null);
  const [message, setMessage] = useState(""); // Success message
  const [formErrors, setFormErrors] = useState({}); // Validation errors
  const [notification, setNotification] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true); // Ensures document.body is available
  }, []);



  // Handlers
  const openModalForAdd = useCallback(() => {
    setEditingEmployeeGrade(null);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const openModalForEdit = useCallback((employeeGrade) => {
    setEditingEmployeeGrade(employeeGrade);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingEmployeeGrade(null);
    setFormErrors({});
    setMessage("");
  }, []);

  const convertFormDataToObject = (formData) => {
    return Object.fromEntries(formData.entries());
  };

  // create and update function
  const handleFormSubmit = useCallback(
    async (employeeGrade) => {
      setLoading(true);
      try {
        // console.log("Submitting employee grade:", employeeGrade);
        if (editingEmployeeGrade) {
          // console.log("employee grade payload", employeeGrade);
        
          // Convert FormData to object
          // console.log(employeeGrade instanceof FormData)
          const employeeGradeData = employeeGrade instanceof FormData ? convertFormDataToObject(employeeGrade) : employeeGrade;
  
          // console.log("Payload being sent:", { ...editingEmployeeGrade, ...employeeGradeData });
  
          await updateEmployeeGrade({ ...editingEmployeeGrade, ...employeeGradeData });
          setMessage("Employee Grade updated successfully.");
          // setNotification("Employee Grade updated successfully.");
          setNotification({
            message: "Employee Grade updated successfully.",
            type: "success",
          });
        } else {
          // console.log("create employee grade", employeeGrade)
          await addEmployeeGrade(employeeGrade);
          setMessage("Employee Grade added successfully.");
          // setNotification("employeeGrade added successfully.");
          setNotification({
            message: "employeeGrade added successfully.",
            type: "success",
          });
        }
        closeModal();
      } catch (err) {
        if (err.errors) {
          setFormErrors(err.errors);
        } else {
          // console.error("Unexpected error during submission:", err);
          // setNotification("Unexpected error during submission:", err);
          setNotification({
            message: "Unexpected error during submission:", err,
            type: "error",
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [editingEmployeeGrade, addEmployeeGrade, updateEmployeeGrade, closeModal]
  );

  // Memoize department names
  const employeeGradeForTable = useMemo(
    () =>
        employeeGrades.map((employeeGrade) => ({
        ...employeeGrade,
        name: employeeGrade?.name || "Unknown Employee Grade",
      })),
    [employeeGrades]
  );

  // delete function
  const handleDeleteEmployeeGrade = async (id) => {
    try {
      await deleteEmployeeGrade(id);
      // setNotification("Employee Grade deleted successfully");
      setNotification({
        message: "Employee Grade deleted successfully",
        type: "success",
      });
    } catch (error) {
      // setNotification("Failed to delete employee Grade");
      setNotification({
        message: "Failed to delete employee Grade",
        type: "error",
      });
    }
  };

  // Table columns
  const columns = useMemo(
    () => [
      { label: "Name", field: "name" },
      {
        label: "Actions",
        field: "actions",
        render: (row) => (
          <ActionsColumn
            row={row}
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            openModalForEdit={openModalForEdit}
            handleDeleteEmployeeGrade={handleDeleteEmployeeGrade}
          />
        ),
      },
    ],
    [
      openMenu,
      openModalForEdit,
      handleDeleteEmployeeGrade,
    ]
  );
  

  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      
      {/* notification compo */}
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-semibold text-black dark:text-white">Employee Grade Master</h2>
        <button
          onClick={openModalForAdd}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 w-full sm:w-auto"
        >
          Add Employee Grade
        </button>
      </div>

      {/* Success Message */}
      {message && (
        <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-md">
          {message}
        </div>
      )}

      {/* Candidates Table */}
      <ReusableTable
        data={employeeGradeForTable}
        loading={isLoading}
        columns={columns}
        filterable={true}
        excludeFilterColumns={["actions"]}
      />

      {/* Modal for Adding or Editing */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        title={editingEmployeeGrade ? "Edit Employee Grade" : "Add Employee Grade"}
      >
        <EmployeeGradeForm
          loading={loading}
          closeModal={closeModal}
          onSubmit={handleFormSubmit}
          initialValues={editingEmployeeGrade || { name: "", description: "", is_active: "" }}
          errors={formErrors}
        />
      </Modal>
    </div>
  );
});

export default EmployeeGradeMasters;
